/**
 * What is actually printed on a resistor.
 *
 * The catalogue knows the resistance; the drawer knows only the marking, and
 * matching the two up by hand is the slowest part of finding a resistor. Both
 * directions are worth showing: the chip code for surface-mount parts and the
 * colour bands for through-hole ones, since the same 4.7 kΩ appears as "472",
 * as "47C" and as yellow-violet-red depending on which one is in the drawer.
 *
 * The tolerance decides which chip code a real part carries: 1% parts use the
 * EIA-96 three-character code, everything looser uses the plain multiplier
 * codes. Both are given when the tolerance is unknown, because guessing which
 * one the part uses is worse than showing the two candidates.
 */

/** E96 significant figures, in the order EIA-96 numbers them (01 = 100). */
const E96 = [
  100, 102, 105, 107, 110, 113, 115, 118, 121, 124, 127, 130, 133, 137, 140, 143, 147, 150,
  154, 158, 162, 165, 169, 174, 178, 182, 187, 191, 196, 200, 205, 210, 215, 221, 226, 232,
  237, 243, 249, 255, 261, 267, 274, 280, 287, 294, 301, 309, 316, 324, 332, 340, 348, 357,
  365, 374, 383, 392, 402, 412, 422, 432, 442, 453, 464, 475, 487, 499, 511, 523, 536, 549,
  562, 576, 590, 604, 619, 634, 649, 665, 681, 698, 715, 732, 750, 768, 787, 806, 825, 845,
  866, 887, 909, 931, 953, 976,
];

/** The multiplier letter in EIA-96, keyed by power of ten. */
const EIA96_MULTIPLIER: Record<number, string> = {
  '-3': 'X',
  '-2': 'Y',
  '-1': 'Z',
  0: 'A',
  1: 'B',
  2: 'C',
  3: 'D',
  4: 'E',
  5: 'F',
};

export const BAND_COLORS = [
  'black',
  'brown',
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'violet',
  'grey',
  'white',
] as const;

export type BandColor = (typeof BAND_COLORS)[number] | 'gold' | 'silver';

const TOLERANCE_BAND: Record<string, BandColor> = {
  '±0.1%': 'violet',
  '±0.25%': 'blue',
  '±0.5%': 'green',
  '±1%': 'brown',
  '±2%': 'red',
  '±5%': 'gold',
  '±10%': 'silver',
};

export interface ResistorMarking {
  /** Three- or four-digit chip code, e.g. "472" or "4701". */
  chip?: string;
  /** EIA-96 code for 1% chip resistors, e.g. "47C". */
  eia96?: string;
  /** Colour bands, four or five of them including the tolerance band. */
  bands?: BandColor[];
  /** "R47" style code, used on chips below 10 Ω where a decimal point cannot
   *  be printed. */
  chipFraction?: string;
}

const round = (value: number, places: number) =>
  Number(value.toFixed(places));

/** Significant digits and the power of ten, for a positive resistance. */
function split(ohms: number, digits: number): { figures: number; exponent: number } | null {
  if (!Number.isFinite(ohms) || ohms <= 0) return null;
  let exponent = Math.floor(Math.log10(ohms)) - (digits - 1);
  let figures = Math.round(ohms / 10 ** exponent);
  // Rounding 999.6 up to four figures gives 10000, which is one digit too many.
  if (figures >= 10 ** digits) {
    figures = Math.round(figures / 10);
    exponent += 1;
  }
  return { figures, exponent };
}

/** The plain chip code: significant digits followed by the number of zeros. */
export function chipCode(ohms: number, digits: 3 | 4): string | null {
  const parts = split(ohms, digits - 1);
  if (!parts) return null;
  const { figures, exponent } = parts;
  if (exponent < 0) {
    // Below 10 Ω the decimal point is printed as an R: 4.7 Ω is "4R7".
    const text = String(round(ohms, digits - 1));
    return text.replace('.', 'R');
  }
  if (exponent > 9) return null;
  return `${figures}${exponent}`;
}

/** EIA-96: two digits for the E96 figure, a letter for the multiplier. */
export function eia96Code(ohms: number): string | null {
  const parts = split(ohms, 3);
  if (!parts) return null;
  const { figures, exponent } = parts;
  const index = E96.indexOf(figures);
  // Only values that are actually in E96 have a code; a 4.7 kΩ from the E24
  // series is not marked this way, and inventing a code for it would be a lie.
  if (index < 0) return null;
  const letter = EIA96_MULTIPLIER[exponent];
  return letter ? `${String(index + 1).padStart(2, '0')}${letter}` : null;
}

/** Colour bands. Four for E24 and coarser, five when three figures are needed. */
export function colorBands(ohms: number, tolerance?: string | null): BandColor[] | null {
  const tight = tolerance ? ['±0.1%', '±0.25%', '±0.5%', '±1%', '±2%'].includes(tolerance) : false;
  const digits = tight ? 3 : 2;
  const parts = split(ohms, digits);
  if (!parts) return null;
  const { figures, exponent } = parts;
  if (exponent < -2 || exponent > 7) return null;

  const bands: BandColor[] = String(figures)
    .split('')
    .map((digit) => BAND_COLORS[Number(digit)] as BandColor);

  bands.push(exponent === -1 ? 'gold' : exponent === -2 ? 'silver' : BAND_COLORS[exponent]!);
  if (tolerance && TOLERANCE_BAND[tolerance]) bands.push(TOLERANCE_BAND[tolerance]!);
  return bands;
}

export function resistorMarking(
  ohms: number | null | undefined,
  tolerance?: string | null,
): ResistorMarking | null {
  if (ohms === null || ohms === undefined || !Number.isFinite(ohms) || ohms <= 0) return null;

  const tight = tolerance === '±1%' || tolerance === '±0.5%' || tolerance === '±0.25%';
  const marking: ResistorMarking = {};

  const three = chipCode(ohms, 3);
  const four = chipCode(ohms, 4);
  if (three && three.includes('R')) marking.chipFraction = three;
  else if (three) marking.chip = tight && four ? four : three;

  const eia = eia96Code(ohms);
  if (eia && (tight || !tolerance)) marking.eia96 = eia;

  const bands = colorBands(ohms, tolerance);
  if (bands) marking.bands = bands;

  return Object.keys(marking).length > 0 ? marking : null;
}
